import React, { useCallback, useState } from 'react';
import useCompanyContract from '../hooks/use-company-contract';
import { useParams } from 'react-router-dom';
import { PageParams } from '../types/PageParams';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { SelectWrapper } from 'src/components/Form/SelectWrapper';
import InputWrapper from 'src/components/Form/InputWrapper';
import { formatDateToNOString } from 'src/utils/FormatValue';
import { capitalizeFirstLetter } from 'src/utils/string';
import useToggle from 'src/hooks/use-toggle';
import ErrorSupportMessage from 'src/features/siva/components/ErrorSupportMessage';
import { requiredI18n } from 'src/utils/validation';
import { convertIdToNumber } from '../utils/idConversion';
import { SivaContractUpdateDto, SivaPhase } from 'src/api/v2';
import { bransjeOptions, ideaSourceOptions } from '../../constants';
import useAdvisorOptions, { SivaAdvisorOption } from '../../hooks/use-advisor-options';
import '../styles/Contract.scss';
import { Link } from 'react-router-dom';
import { useSupportEmail } from 'src/hooks/use-support-email';
import ConnectCompany from './ConnectCompany';
import useGeonorge, { Municipality } from 'src/features/one-way-in/hooks/use-geonorge';
import { Autocomplete } from 'src/components/Autocomplete/Autocomplete';
import ValueField from 'src/components/ValueField/ValueField';
import { useAuth } from 'src/features/authorization';
import { Heading, Textarea, Label, Button, Tag, Icon, Alert } from '@innovationnorway/cl-react';
import DialogContainer from 'src/components/DialogContainer/DialogContainer';
import SelfDeclarationDialog from '../../components/SelfDeclarationDialog';
import { useSelfDeclarationDisplay } from '../hooks/use-self-declaration-display';
import '../styles/Contract.scss';

type Props = {
  onCancel: () => void;
};

const EditContract: React.FC<Props> = ({ onCancel }) => {
  const { contractId } = useParams<PageParams>();
  const {
    contract,
    contractAdmissionDate,
    currentPhase,
    admissionFormDate,
    enrouteFormDate,
    exitFormDate,
    updateSivaContractMutation,
    company,
  } = useCompanyContract(convertIdToNumber(contractId));
  const { checkAccess } = useAuth();
  const isBusinessGarden = checkAccess('UI_SivaBusinessGarden').read;

  // Dette gjør at vi kan refetch rådgivere i et intervall, etter at bruker har trykt på linken for Tilgangsstyring
  const [refetchInterval, setRefetchInterval] = useState(0);
  const REFETCH_INTERVAL = 1000 * 15;
  const startRefetchInterval = useCallback(() => setRefetchInterval(REFETCH_INTERVAL), [REFETCH_INTERVAL]);
  const { advisorOptions } = useAdvisorOptions({
    refetchInterval,
  });
  const { municipalities, getMunicipalityNameByNumber } = useGeonorge();

  const [companyError, setCompanyError] = useState(false);
  const [orgNumber, setOrgNumber] = useState('');
  const [selectedCompanyName, setSelectedCompanyName] = useState('');

  const { t: tCommon } = useTranslation();
  const { t: tSiva } = useTranslation('siva');
  const { t: tError } = useTranslation('error');
  const [updateError, setUpdateError] = useState(false);
  const [showSelfDeclarationDialog, setShowSelfDeclarationDialog] = useState(false);

  const { latestSelfDeclaration, statusText, statusColor, validToDate, validToText } =
    useSelfDeclarationDisplay(contract?.selfDeclarations);

  const { isActive, activate, deactivate } = useToggle(false);
  const { email, mailto } = useSupportEmail();

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm<SivaContractUpdateDto>({
    shouldFocusError: true,
    defaultValues: {
      ...contract,
    },
  });

  const onSubmit = handleSubmit(async (updateDto: SivaContractUpdateDto) => {
    await updateSivaContractMutation
      .mutateAsync({
        ...updateDto,
        organizationNumber: orgNumber,
      })
      .then(() => {
        setUpdateError(false);
      })
      .catch(() => {
        setUpdateError(true);
      })
      .finally(() => {
        activate();
      });
  });

  return (
    <div className="width--60">
      <Heading level={2} className="margin-bottom--5">
        {tSiva('company.details.contract.editContract')}
      </Heading>

      <form onSubmit={onSubmit} className="stack">
        <div>
          {currentPhase?.sivaPhase === SivaPhase.Idea ? (
            <Heading level={3}>{tSiva('idea.description')}</Heading>
          ) : (
            <Heading level={3}>{tSiva('newCompany.contract.section.info.title')}</Heading>
          )}

          {!company && (
            <div>
              <span className="font-weight--bold">{tSiva('contract.edit.connectCompany')}</span>
              {currentPhase && (
                <ConnectCompany
                  phase={currentPhase.sivaPhase}
                  selectedCompanyName={selectedCompanyName}
                  orgNumber={orgNumber}
                  setOrgNumber={setOrgNumber}
                  setSelectedCompanyName={setSelectedCompanyName}
                  setCompanyError={setCompanyError}
                  showNoCompanyCheckbox={true}
                />
              )}
            </div>
          )}

          <div>
            <span className="font-weight--bold">{tCommon('phase')}</span>
          </div>
          <div>
            {tSiva(`phase.${currentPhase?.sivaPhase}` as any, {
              defaultValue: currentPhase?.sivaPhase || '',
            })}
          </div>
        </div>
        <div>
          <SelectWrapper
            label={`${tCommon('industry')} ${currentPhase?.sivaPhase === SivaPhase.Idea ? '' : '*'}`}
            {...register('externalIndustry', {
              required: currentPhase?.sivaPhase === SivaPhase.Idea ? undefined : requiredI18n(tError),
            })}
            error={errors?.externalIndustry?.message}
            options={bransjeOptions}
          />
        </div>
        <div>
          <InputWrapper
            label={`${tSiva('company.details.contract.overview.ideaName')} *`}
            {...register('name', { required: requiredI18n(tError) })}
            error={errors?.name?.message}
          />
        </div>
        <div>
          <SelectWrapper
            label={`${tSiva('company.details.contract.overview.ideaSource')} *`}
            {...register('ideaSource', {
              required: requiredI18n(tError),
            })}
            error={errors?.ideaSource?.message}
            options={ideaSourceOptions}
          />
        </div>
        <div>
          <div className="display--flex align-items--center gap--1">
            <span className="font-weight--bold">
              {tSiva('company.details.contract.overview.selfDeclaration')}
            </span>
            {company?.organizationNumber && (
              <Button
                variant="tertiary"
                aria-label={`${tCommon('edit')} ${tCommon('delivered').toLowerCase()}`}
                type="button"
                onClick={() => setShowSelfDeclarationDialog(true)}
              >
                <Icon prefix="fasr" iconName="pencil" />
              </Button>
            )}
          </div>
          {company?.organizationNumber ? (
            <>
              <Tag data-color={statusColor}>{'' + statusText}</Tag>
              {validToDate && (
                <p>
                  {tSiva('company.details.contract.overview.selfDeclarationValidTo', {
                    date: validToText,
                  })}
                </p>
              )}
              {contract && (
                <SelfDeclarationDialog
                  show={showSelfDeclarationDialog}
                  onClose={() => setShowSelfDeclarationDialog(false)}
                  sivaContractId={contract.id}
                  organizationNumber={company.organizationNumber}
                  contractNumericId={contract.numericId}
                  initialSelfClassification={latestSelfDeclaration?.selfClassification ?? []}
                  existingDeliveredDate={latestSelfDeclaration?.deliveredDate}
                  acceptanceDate={contractAdmissionDate}
                />
              )}
            </>
          ) : (
            <Tag data-color={'neutral' as const}>{tCommon('notApplicable')}</Tag>
          )}
        </div>
        {contract?.phases.at(-1)?.sivaPhase !== SivaPhase.Idea && (
          <>
            <Alert data-color="warning">
              {`${tSiva('company.details.contract.editContract.admissionNote')} `}
              <a className="siva-contract-overview__back-link" href={mailto} title="support@enveiinn.no">
                {email}
              </a>
            </Alert>
            <div>
              <div>
                <span className="font-weight--bold">
                  {tSiva('company.details.contract.overview.admissionDate')}
                </span>
              </div>
              <div>
                {contractAdmissionDate &&
                  formatDateToNOString(new Date(contractAdmissionDate), false, '{date}.{month}.{year}', true)}
              </div>
            </div>
            <div>
              <div>
                <span className="font-weight--bold">
                  {tSiva('company.details.contract.overview.dispatchAdmissionForm')}
                </span>
              </div>
              <div>
                {!!admissionFormDate &&
                  capitalizeFirstLetter(
                    formatDateToNOString(new Date(admissionFormDate), true, '{month} {year}', true),
                  )}
              </div>
            </div>
            <div>
              <div>
                <span className="font-weight--bold">
                  {tSiva('company.details.contract.overview.dispatchEnRouteForm')}
                </span>
              </div>
              <div>
                {!!enrouteFormDate &&
                  capitalizeFirstLetter(
                    formatDateToNOString(new Date(enrouteFormDate), true, '{month} {year}', true),
                  )}
              </div>
            </div>
          </>
        )}
        {!!exitFormDate && (
          <div>
            <div>
              <span className="font-weight--bold">
                {tSiva('company.details.contract.overview.dispatchExitForm')}
              </span>
            </div>
            <div>
              {capitalizeFirstLetter(
                formatDateToNOString(new Date(exitFormDate), true, '{month} {year}', true),
              )}
            </div>
          </div>
        )}
        <div className="margin-top--6">
          <Heading level={3}>{tCommon('contact')}</Heading>
          <p>{tSiva('newCompany.contract.section.contact.description')}</p>
        </div>
        <div className="display--flex gap--4 justify-content--space-between">
          <div>
            <InputWrapper
              label={`${tCommon('firstName')} *`}
              {...register('contactFirstName', { required: requiredI18n(tError) })}
              error={errors?.name?.message}
            />
          </div>
          <div>
            <InputWrapper
              label={`${tCommon('lastName')} *`}
              {...register('contactLastName', { required: requiredI18n(tError) })}
              error={errors?.name?.message}
            />
          </div>
        </div>
        <div>
          <InputWrapper
            label={`${tCommon('email')} *`}
            {...register('contactEmail', { required: requiredI18n(tError) })}
            error={errors?.name?.message}
          />
        </div>
        {!isBusinessGarden && currentPhase?.sivaPhase === SivaPhase.Idea && (
          <>
            {watch('municipality') === undefined ? (
              <Controller
                control={control}
                name="municipality"
                render={({ field }) => (
                  <Autocomplete<Municipality>
                    label={tSiva('newCompany.contract.form.municipality.label')}
                    name={field.name}
                    items={municipalities.sort((a, b) => a.name.localeCompare(b.name))}
                    filter={(item, value: string): boolean => {
                      const lowerCaseValue = value.toLowerCase();

                      return item.name.toLowerCase().includes(lowerCaseValue) || false;
                    }}
                    onItemSelected={(muni) => field.onChange(muni.number)}
                    itemLabel={(muni) => muni.name}
                    renderItem={(item) => <span>{item.name}</span>}
                  />
                )}
              />
            ) : (
              <ValueField
                label={tSiva('newCompany.contract.form.municipality.label')}
                value={
                  <div className="display--flex gap--2 align-items--center">
                    <span>{getMunicipalityNameByNumber(watch('municipality'))}</span>
                    <Button
                      variant="tertiary"
                      aria-label={`${tCommon('edit')} ${tCommon('municipality').toLowerCase()}`}
                      type="button"
                      onClick={() => setValue('municipality', undefined)}
                    >
                      <Icon prefix="fasr" iconName="pencil" />
                    </Button>
                  </div>
                }
              />
            )}
          </>
        )}
        <div className="margin-top--6 ">
          <Heading level={3}>{tSiva('newCompany.contract.section.followUp.title')}</Heading>
          <Controller
            control={control}
            name="advisorId"
            render={({ field }) => (
              <Autocomplete<SivaAdvisorOption>
                label={tCommon('advisor')}
                name={field.name}
                items={advisorOptions}
                filter={(item, value: string): boolean => {
                  const lowerCaseValue = value.toLowerCase();

                  return item.text.toLowerCase().includes(lowerCaseValue) || false;
                }}
                onItemSelected={(advisor) => field.onChange(advisor.value)}
                itemLabel={(advisor) => `${advisor.text}`}
                renderItem={(advisor) => `${advisor.text}`}
                defaultValue={advisorOptions.find((x: SivaAdvisorOption) => x.value === field.value)?.text}
              />
            )}
          />
          <p className="margin-top--0 margin-bottom--4">
            {tSiva('newCompany.contract.form.advisorId.link.text')}{' '}
            <Link
              to={`/actor-profile/settings/access`}
              target="_blank"
              onClick={() => {
                startRefetchInterval();
              }}
            >
              <span>{tCommon('accessControl')}</span>
            </Link>
            .
          </p>
        </div>
        <div className="font-size--x-large">
          <Label htmlFor="note" weight="semibold">
            {tCommon('note')}
          </Label>
          <Textarea id="note" {...register('notes')} maxLength={500} rows={4} />
        </div>
        <div className="display--flex gap--1 justify-content--flex-start margin-top--7">
          <Button
            type="submit"
            loading={updateSivaContractMutation.isLoading}
            disabled={updateSivaContractMutation.isLoading || companyError}
          >
            {tCommon('save')}
          </Button>
          <Button
            variant="secondary"
            type="button"
            onClick={onCancel}
            loading={updateSivaContractMutation.isLoading}
            disabled={updateSivaContractMutation.isLoading}
          >
            {tCommon('cancel')}
          </Button>
        </div>
      </form>
      <DialogContainer
        show={isActive}
        onClose={deactivate}
        title={
          updateError
            ? tSiva('company.details.contract.editContract.errormodal.header')
            : tCommon('confirmation')
        }
      >
        {!updateError && <div>{tSiva('company.details.contract.editContract.successmodal.text')}</div>}
        {!!updateError && <ErrorSupportMessage />}
        <Button className="margin-top--6" type="button" onClick={() => deactivate()}>
          {tCommon('close')}
        </Button>
      </DialogContainer>
    </div>
  );
};

export default EditContract;
